import PageLoader from "@/components/ui/PageLoader";
import { Button } from "@/components/ui/button/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import SubmitButton from "@/components/ui/submit-button";
import Typography from "@/components/ui/typography";
import { useAuth } from "@/context/auth-context";
import useDeleteBankAccount from "@/features/bank-accounts/useDeleteBankAccount";
import useGetBankAccounts from "@/features/bank-accounts/useGetBankAccounts";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter, useLoaderData } from "@tanstack/react-router";
import { SquarePlus } from "lucide-react";
import { useState } from "react";
import { ArrowLeft, X, Info } from "lucide-react";
import { SettingsContainer } from "@/components/ui/SettingsContainer";

export default function LinkedAccountsPage() {
	const { user } = useAuth();
	const { location_id } = useLoaderData({ from: "__root__" });
	const bankAccountsQuery = useGetBankAccounts({
		guestId: user.id,
		locationId: location_id,
	});
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const router = useRouter();
	const [deletingAccountId, setDeletingAccountId] = useState<number | null>(
		null,
	);

	const deleteBankAccountMutation = useDeleteBankAccount({
		userId: user.id,
		locationId: location_id,
		queryClient,
		refetchBankAccounts: bankAccountsQuery.refetch,
	});

	const handleDelete = () => {
		if (!deletingAccountId) return;
		deleteBankAccountMutation.mutate({
			bankAccountId: deletingAccountId,
		});
		setDeletingAccountId(null);
	};

	return (
		<>
			<PageLoader isFullscreenLoading={true} query={bankAccountsQuery}>
				{(bankAccounts) => {
					return (
						<SettingsContainer>
							<div className="mb-8 mt-4 flex justify-end md:justify-start">
								<Button
									variant="ghost"
									size="compact"
									className="self-start"
									onClick={() => {
										router.history.back();
									}}
								>
									<X className="text-textDark md:hidden" />
									<ArrowLeft className="text-textDark hidden md:block" />
								</Button>
							</div>
							<Typography className="mb-4" variant={"h4"} weight={"semibold"}>
								Linked Accounts
							</Typography>
							<div className="mb-6 flex items-start gap-3 rounded-default border border-primary-100 bg-primary-50 p-4">
								<Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
								<p className="text-sm text-textLight">
									The account number shown here may not match the one at your
									bank. Plaid sometimes issues a virtual account number that
									keeps your real bank details private while still letting
									transfers go through normally.{" "}
									<a
										href="https://support-my.plaid.com/hc/en-us/articles/19856062163863-What-are-Virtual-Account-Numbers"
										target="_blank"
										rel="noopener noreferrer"
										aria-label="View more about virtual account numbers (opens in new tab)"
										className="font-semibold text-textDark underline hover:opacity-80"
									>
										View more.
									</a>
								</p>
							</div>

							<div className="">
								{bankAccounts.map((bankAccount) => {
									return (
										<div
											key={bankAccount.id}
											className="flex items-center justify-between border-b border-muted py-2"
										>
											<div className="flex items-center gap-2">
												{/* <Avatar>
													<AvatarImage
														className="h-10 w-10 rounded-full"
														src={idx % 2 === 0 ? "/account1.png" : "/account2.png"}
													/>
													<AvatarFallback>CN</AvatarFallback>
												</Avatar> */}
												<div>
													<Typography
														variant={"h5"}
														className="leading-[100%]"
														weight={"medium"}
													>
														{bankAccount.bank_account ?? ""}
													</Typography>
													<Typography
														variant={"p"}
														color={"primary-light"}
														weight={"medium"}
													>
														{bankAccount.bank_name ?? ""}
													</Typography>
												</div>
											</div>
											<Button
												variant={"link"}
												className="text-base text-textLight"
												onClick={() => setDeletingAccountId(bankAccount.id)}
											>
												Unlink
											</Button>
										</div>
									);
								})}
							</div>
							<div className="border-b border-muted py-2">
								<Button
									variant={"link"}
									className="pl-0 text-base text-textDark font-normal hover:no-underline"
									onClick={() => {
										navigate({ to: "/main/setting/linked-accounts/new" });
									}}
								>
									<SquarePlus />
									Add account
								</Button>
							</div>
						</SettingsContainer>
					);
				}}
			</PageLoader>
			<Dialog
				open={deletingAccountId != null}
				onOpenChange={() => setDeletingAccountId(null)}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							<Typography variant={"h4"}>Remove Bank Account</Typography>
						</DialogTitle>
						<DialogDescription>
							<Typography variant={"p"}>
								Are you sure you want to remove this bank account? This action
								cannot be undone.
							</Typography>
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="">
						<Button
							size={"md"}
							className=""
							variant="outline"
							onClick={() => setDeletingAccountId(null)}
						>
							Cancel
						</Button>

						<SubmitButton
							mutationStatus={deleteBankAccountMutation.status}
							loadingText="Removing..."
							type="button"
							onClick={() => {
								handleDelete();
							}}
							size={"md"}
							className="mb-4"
							variant="destructive"
						>
							Remove
						</SubmitButton>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
